import React, { useEffect, useState, useRef } from 'react';
import { RefreshCw, X } from 'lucide-react';

/**
 * UpdateAvailableBanner — non-disruptive toast that appears when the Service
 * Worker has detected and installed a new version of the app. Tapping "Refresh
 * now" calls the SW's SKIP_WAITING message, which triggers a controllerchange
 * and reloads the page with the new bundles.
 *
 * Listens for the `enferno:sw-update-available` window event dispatched by
 * `lib/swRegister.js`. Always rendered globally (App.js) so it can surface
 * the moment a new version arrives regardless of route.
 *
 * Dismissable for now-only — the SW will still apply the update on the next
 * full app launch, and if the user reopens during the same session the toast
 * will re-appear from a fresh updatefound event.
 */
const UpdateAvailableBanner = () => {
  const [visible, setVisible] = useState(false);
  const applyRef = useRef(null);

  useEffect(() => {
    const handler = (event) => {
      applyRef.current = event?.detail?.applyUpdate || null;
      setVisible(true);
    };
    window.addEventListener('enferno:sw-update-available', handler);
    return () => window.removeEventListener('enferno:sw-update-available', handler);
  }, []);

  if (!visible) return null;

  const refresh = () => {
    if (applyRef.current) {
      applyRef.current();
    } else {
      window.location.reload();
    }
  };

  return (
    <div
      className="fixed bottom-4 inset-x-4 z-[100] md:bottom-6 md:left-auto md:right-6 md:inset-x-auto md:max-w-sm"
      data-testid="sw-update-banner"
      role="status"
      aria-live="polite"
    >
      <div className="flex items-start gap-3 bg-enf-surface-deep border border-enf-accent-fg/40 rounded-xl shadow-2xl p-4">
        <div className="w-9 h-9 rounded-full bg-enf-accent/15 border border-enf-accent-fg/30 flex items-center justify-center flex-shrink-0">
          <RefreshCw className="w-4 h-4 text-enf-accent-fg" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-[0.3em] text-enf-accent-fg font-semibold leading-none mb-1">
            Update ready
          </div>
          <div className="text-sm text-enf-text-primary leading-snug mb-2">
            A new version of Enferno is installed and waiting.
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={refresh}
              data-testid="sw-update-refresh"
              className="px-3 py-1.5 bg-enf-accent hover:bg-enf-accent-hover text-enf-accent-contrast rounded-md text-xs font-semibold uppercase tracking-wider transition-colors"
            >
              Refresh now
            </button>
            <button
              onClick={() => setVisible(false)}
              data-testid="sw-update-dismiss"
              className="px-3 py-1.5 text-xs text-enf-text-muted hover:text-enf-text-primary"
            >
              Later
            </button>
          </div>
        </div>
        <button
          onClick={() => setVisible(false)}
          aria-label="Dismiss"
          className="p-1 text-enf-text-muted hover:text-enf-text-primary"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default UpdateAvailableBanner;
